import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { adminService } from '../../services/adminService';
import { metricsService } from '../../services/metricsService';
import SubscriptionPanel from './SubscriptionPanel';
import PersonalQuotaFields from './PersonalQuotaFields';
import MinuteRateFields, { buildMinuteRate, currentMonthMsk } from './MinuteRateFields';
import { IS_SAAS } from '../../config/edition';
import { formatMoscowDate } from '../../lib/moscowDate';
import {
  ROLES, ROLE_LABELS, QUOTA_DEFAULTS,
  QUEUE_CLASSES, QUEUE_CLASS_LABELS, QUEUE_CLASS_ICONS,
  basePriority, defaultQueueClass,
} from '../../lib/roles';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Строка о согласии с офертой: дата, редакция и канал последнего акцепта. */
function LegalLine({ email }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!email) return undefined;
    let alive = true;
    adminService.getUserLegal(email)
      .then((list) => { if (alive) setItems(list); })
      .catch(() => { if (alive) setItems(null); });
    return () => { alive = false; };
  }, [email]);
  if (items === null) return null;
  if (items.length === 0) {
    return <p className="text-sm text-paper-ink-3">Оферта и согласие не приняты</p>;
  }
  const last = items[0];
  return (
    <p className="text-sm text-paper-ink-3">
      Оферта и согласие приняты {formatMoscowDate(last.accepted_at)}, ред. от {formatMoscowDate(last.offer)} ({last.channel})
    </p>
  );
}

/** Сворачиваемая секция карточки: открыта всегда ровно одна. */
function CardSection({ id, title, open, onToggle, children }) {
  return (
    <div className="rounded-chip bg-paper-bg">
      <button
        data-testid={`section-${id}`}
        type="button"
        className="w-full flex justify-between items-center px-3 py-2 text-sm font-medium text-paper-ink-2 hover:bg-paper-panel"
        onClick={() => onToggle(open ? '' : id)}
      >
        {title}
        <span className="text-paper-hint">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="px-3 pb-3 space-y-4">{children}</div>}
    </div>
  );
}


export default function EditUserDialog({ user, open, onClose, onSave, offices = [] }) {
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('user');
  const [queueClass, setQueueClass] = useState('normal');
  const [quotaAudio, setQuotaAudio] = useState('');
  const [quotaSummary, setQuotaSummary] = useState('');
  const [quotaHourly, setQuotaHourly] = useState('');
  const [minuteRate, setMinuteRate] = useState('');
  const [minuteSince, setMinuteSince] = useState(currentMonthMsk());
  const [expiration, setExpiration] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');  // ответственный за учётку и её ключи
  const [dismissed, setDismissed] = useState(false); // отключён, потому что уволен
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [office, setOffice] = useState(null);       // контрагенту — правится, сотруднику read-only
  const [initialOffice, setInitialOffice] = useState(null);
  const [officeLoading, setOfficeLoading] = useState(false);
  // Карточка разбита на секции: одним столбцом она не помещалась на экран.
  const [openSection, setOpenSection] = useState('account');
  const [person, setPerson] = useState(null);       // GAL: {name, department, job_title}

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || '');
      setRole(user.role || 'user');
      setQueueClass(user.queue_class || defaultQueueClass(user.role || 'user'));
      setQuotaAudio(user.quota_audio_minutes != null ? String(user.quota_audio_minutes) : '');
      setQuotaSummary(user.quota_summary_requests != null ? String(user.quota_summary_requests) : '');
      setQuotaHourly(user.quota_summary_per_hour != null ? String(user.quota_summary_per_hour) : '');
      setMinuteRate(user.minute_rate != null ? String(user.minute_rate).replace('.', ',') : '');
      setMinuteSince(currentMonthMsk());
      setExpiration(user.expiration ? new Date(user.expiration).toISOString().slice(0, 10) : '');
      setOwnerEmail(user.owner_email || '');
      setDismissed(user.deactivation?.reason === 'dismissed');
      setError(null);
      // Офис зависит от email: контрагент — из office_map, сотрудник — из org-directory.
      setOffice(null);
      setPerson(null);
      if (user.email) {
        setOfficeLoading(true);
        metricsService.resolveEnrichment([user.email])
          .then(({ offices: resolved, people }) => {
            const own = resolved[user.email.toLowerCase()] || null;
            setOffice(own);
            setInitialOffice(own);
            setPerson(people[user.email.toLowerCase()] || null);
          })
          .catch(() => {})
          .finally(() => setOfficeLoading(false));
      }
    }
  }, [user]);

  // Контрагент — по ВЫБРАННОЙ роли, чтобы при переводе сотрудника в client/partner
  // офис и ответственного можно было заполнить в том же диалоге. Партнёр — тоже
  // контрагент: свои ключи и свою квоту, а не учётку внутри штата.
  const isContractor = role === 'client' || role === 'service' || role === 'partner';
  // Уволиться может только сотрудник, и только отключённый: включённому отметка ни к чему.
  const canMarkDismissed = user && user.is_active === false
    && ['user', 'admin'].includes(user.role || 'user');

  // Смена роли обязана переставить и класс очереди. Иначе перевод user → service
  // без касания селекта не отправлял queue_class вовсе: в документе класса нет,
  // /check-token отдаёт дефолт роли — «Пакетный», — а карточка всё это время
  // показывала «Обычный». С кэпом пакетных слотов такая учётка молча заперта
  // в один слот, и по карточке этого не видно.
  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setQueueClass(defaultQueueClass(newRole));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const fields = {};
      if (displayName !== (user.display_name || '')) fields.display_name = displayName || null;
      if (role !== (user.role || 'user')) fields.role = role;
      const oldQueueClass = user.queue_class || defaultQueueClass(user.role || 'user');
      if (queueClass !== oldQueueClass) fields.queue_class = queueClass;
      const newAudio = quotaAudio === '' ? null : parseInt(quotaAudio, 10);
      const oldAudio = user.quota_audio_minutes ?? null;
      if (newAudio !== oldAudio) fields.quota_audio_minutes = newAudio;
      const newSummary = quotaSummary === '' ? null : parseInt(quotaSummary, 10);
      const oldSummary = user.quota_summary_requests ?? null;
      if (newSummary !== oldSummary) fields.quota_summary_requests = newSummary;
      const newHourly = quotaHourly === '' ? null : parseInt(quotaHourly, 10);
      const oldHourly = user.quota_summary_per_hour ?? null;
      if (newHourly !== oldHourly) fields.quota_summary_per_hour = newHourly;

      if (ownerEmail.trim() !== (user.owner_email || '')) fields.owner_email = ownerEmail.trim();
      if (canMarkDismissed && dismissed !== (user.deactivation?.reason === 'dismissed')) {
        fields.dismissed = dismissed;
      }
      // Офис контрагента правится здесь же; у сотрудника он read-only (из GAL).
      if (isContractor && office !== null && office !== initialOffice) fields.office = office;
      if (isContractor) {
        const rate = buildMinuteRate(minuteRate, minuteSince, user.minute_rate ?? null);
        if (rate === 'invalid') {
          setError('Цена минуты — число больше нуля, например 0,7');
          setSaving(false);
          return;
        }
        if (rate) fields.minute_rate = rate;
      }

      const oldExp = user.expiration ? new Date(user.expiration).toISOString().slice(0, 10) : '';
      if (expiration !== oldExp) {
        fields.expiration = expiration ? new Date(expiration + 'T23:59:59Z').toISOString() : null;
      }

      if (Object.keys(fields).length === 0) {
        onClose();
        return;
      }

      await onSave(user.email, fields);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const defaults = QUOTA_DEFAULTS[role] || QUOTA_DEFAULTS.user;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{user?.email}</DialogTitle>
          <p className="text-sm text-paper-ink-3">Зарегистрирован {formatDate(user?.created_at)}</p>
          {IS_SAAS && <LegalLine email={user?.email} />}
        </DialogHeader>

        <div data-testid="dialog-body" className="space-y-2 py-2">
          <CardSection id="account" title="Учётка" open={openSection === 'account'} onToggle={setOpenSection}>
          <div>
            <label className="text-xs text-paper-ink-3 block mb-1">Отображаемое имя</label>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Не указано" />
          </div>
          <div>
            <label className="text-xs text-paper-ink-3 block mb-1">Роль</label>
            <select
              data-testid="role-select"
              className="w-full px-3 py-2 rounded-chip bg-paper-bg text-sm"
              value={role}
              onChange={(e) => handleRoleChange(e.target.value)}
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-paper-ink-3 block mb-1">Класс очереди</label>
            <select
              data-testid="queue-class-select"
              className="w-full px-3 py-2 rounded-chip bg-paper-bg text-sm"
              value={queueClass}
              onChange={(e) => setQueueClass(e.target.value)}
            >
              {QUEUE_CLASSES.map((c) => (
                <option key={c} value={c}>{QUEUE_CLASS_ICONS[c]} {QUEUE_CLASS_LABELS[c]}</option>
              ))}
            </select>
            <p className="text-[11px] text-paper-hint mt-1">
              По умолчанию для этой роли: {QUEUE_CLASS_LABELS[defaultQueueClass(role)]}
            </p>
          </div>
          <div className="rounded-chip bg-paper-bg p-3 space-y-1 bg-paper-panel">
            <div className="text-xs text-paper-ink-3">Базовый приоритет</div>
            <div data-testid="base-priority-value" className="text-2xl font-semibold text-paper-ink">
              {basePriority(role, queueClass)}
            </div>
            {/* Роль и класс раскладываются из basePriority(), а не задаются числом
                напрямую: эти цифры сверены с бэкендом (shared/priority_queue.py) и
                своих копий заводить нельзя. */}
            <div className="text-xs text-paper-ink-2">
              роль «{ROLE_LABELS[role]}» {basePriority(role, 'normal')}
            </div>
            {/* mod === 0 у «Обычного» класса — «+0» не печатаем, там и так ничего
                не меняется: строка целиком лишняя, а не просто некрасивое число. */}
            {basePriority(role, queueClass) - basePriority(role, 'normal') !== 0 && (
              <div className="text-xs text-paper-ink-2">
                класс «{QUEUE_CLASS_LABELS[queueClass]}»{' '}
                {(() => {
                  const mod = basePriority(role, queueClass) - basePriority(role, 'normal');
                  return mod >= 0 ? `+${mod}` : String(mod);
                })()}
              </div>
            )}
            <div className="text-xs text-paper-ink-3">
              клиент в кабинете {basePriority('client', 'interactive')}
              {' · '}сотрудник {basePriority('user', 'normal')}
              {' · '}пакетный поток партнёра {basePriority('partner', 'batch')}
            </div>
            <p className="text-[11px] text-paper-hint">
              К базе добавляются ситуативные надбавки: квота в норме +50, короткий файл до +30,
              мало задач за день до +30, ожидание +10 за каждую минуту в очереди.
            </p>
            {role === 'partner' && queueClass === 'interactive' && (
              <p
                data-testid="above-employees-warning"
                className="text-xs text-paper-warning bg-paper-panel rounded p-2"
              >
                Эта учётка встанет в очереди выше сотрудников.
              </p>
            )}
          </div>
          {isContractor && (
            <div>
              <label htmlFor="edit-owner" className="text-xs text-paper-ink-3 block mb-1">
                Ответственный за учётку *
              </label>
              <Input
                id="edit-owner"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="ivanov@1cbit.ru"
              />
              <p className="text-[11px] text-paper-hint mt-1">
                Сотрудник Newton, отвечающий за эту учётку и её ключи. Не путать с офисом:
                офис — куда относить расход.
              </p>
            </div>
          )}
          {canMarkDismissed && (
            <div>
              <label className="flex items-center gap-2 text-sm text-paper-ink">
                <input
                  type="checkbox"
                  checked={dismissed}
                  onChange={(e) => setDismissed(e.target.checked)}
                />
                Уволен
              </label>
              <p className="text-[11px] text-paper-hint mt-1">
                При входе увидит предложение перейти в клиентский Ньютон, а не «обратитесь
                к администратору».
                {!user.deactivation?.reason
                  && ' Причина не записана — пока решает справочник сотрудников.'}
              </p>
            </div>
          )}
          <div>
            <label className="text-xs text-paper-ink-3 block mb-1">Токен действителен до</label>
            <Input type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)} />
          </div>
          </CardSection>

          <CardSection id="billing" title="Тариф и квоты" open={openSection === 'billing'} onToggle={setOpenSection}>
          {/* Тариф — только у client: ветка тарифов в bit-metrics работает
              для этой роли, остальным лимиты по-прежнему задаёт роль. */}
          {/* Подписка — коммерция: в издании onprem её нет, как нет и bit-billing. */}
          {IS_SAAS && role === 'client' && user?.email && <SubscriptionPanel email={user.email} />}
          <PersonalQuotaFields
            audio={quotaAudio}
            summary={quotaSummary}
            hourly={quotaHourly}
            onAudioChange={setQuotaAudio}
            onSummaryChange={setQuotaSummary}
            onHourlyChange={setQuotaHourly}
            defaults={defaults}
          />
          {isContractor && (
            <MinuteRateFields
              rate={minuteRate}
              since={minuteSince}
              history={user?.minute_rates || []}
              onRateChange={setMinuteRate}
              onSinceChange={setMinuteSince}
            />
          )}
          </CardSection>

          <CardSection id="attribution" title="Атрибуция" open={openSection === 'attribution'} onToggle={setOpenSection}>
          <div>
            <label htmlFor="edit-office" className="text-xs text-paper-ink-3 block mb-1">Офис</label>
            {isContractor ? (
              <select
                id="edit-office"
                className="w-full px-3 py-2 rounded-chip bg-paper-bg text-sm"
                value={office || ''}
                onChange={(e) => setOffice(e.target.value)}
                disabled={officeLoading}
              >
                <option value="">— выберите офис —</option>
                {offices.map((o) => (
                  <option key={o.office} value={o.office}>{o.office}</option>
                ))}
              </select>
            ) : (
              <div className="w-full px-3 py-2 bg-paper-panel rounded-chip text-sm text-paper-ink-2">
                {officeLoading ? 'Определяется…' : (office || '— не определён')}
              </div>
            )}
            <p className="text-[11px] text-paper-hint mt-1">
              {isContractor
                ? 'Куда относить расход контрагента. Не путать с ответственным — тот отвечает за учётку.'
                : 'Офис сотрудника приходит из адресной книги (GAL) и вручную не задаётся.'}
            </p>
            {person && (person.department || person.job_title || person.name) && (
              <div className="mt-2 text-xs text-paper-ink-3">
                <div className="font-medium text-paper-hint mb-0.5">Из GAL</div>
                {person.name && <div>ФИО: {person.name}</div>}
                {person.department && <div>Отдел: {person.department}</div>}
                {person.job_title && <div>Должность: {person.job_title}</div>}
              </div>
            )}
          </div>
          </CardSection>
        </div>

        {error && (
          <div className="bg-paper-accent/[0.06] text-paper-accent rounded p-3 text-sm">{error}</div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Отмена</Button>
          <Button className="bg-paper-accent hover:bg-paper-accent-hover" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
